import React from 'react';

import { FontAwesomeIcon } from '@schoolinks/ui-components';
import cn from 'classnames';
import { noop } from 'lodash';

import {
  bool,
  element,
  string,
  oneOf,
  func,
  shape,
  oneOfType,
} from 'prop-types';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';

import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';

import s from './ShadowButton.css';

const colorClass = {
  green: s.green,
  blue: s.blue,
  gray: s.gray,
  red: s.red,
  white: s.white,
};

const variantClass = {
  outlined: s.outlined,
  filled: s.filled,
  text: s.text,
};

const sizeClass = {
  large: s.large,
  medium: s.medium,
  small: s.small,
};

const ShadowButton = ({
  icon,
  label,
  color,
  size,
  onClick,
  className,
  classes,
  variant,
  disabled,
  hasIconLeft,
  hasIconRight,
  tagName: Tag,
  ...restProps
}) => {
  const commonIconMapping = {
    arrow: (
      <FontAwesomeIcon icon={faArrowRight} rotation={hasIconLeft ? 180 : 0} />
    ),
    chevron: (
      <FontAwesomeIcon
        icon={hasIconRight || hasIconLeft ? faChevronRight : faChevronDown}
        rotation={hasIconLeft ? 180 : 0}
      />
    ),
    edit: <FontAwesomeIcon icon={faPencil} />,
    plus: <FontAwesomeIcon icon={faPlus} />,
    trash: <FontAwesomeIcon icon={faTrashCan} />,
    lock: <FontAwesomeIcon icon={faLock} />,
    download: <FontAwesomeIcon icon={faArrowDownToLine} />,
  };

  const selectedIcon =
    typeof icon === 'string' ? commonIconMapping[icon] : icon;

  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      disabled={disabled}
      className={cn(
        s.root,
        colorClass[color],
        variantClass[variant] || s.outlined,
        sizeClass[size],
        {
          [s.rightIcon]: hasIconRight,
          [s.disabled]: disabled,
          [s.noLabelContainer]: !label,
        },
        className,
      )}
      onClick={onClick}
      {...restProps}
    >
      {icon && (hasIconLeft || hasIconRight) ? (
        <div className={cn(s.icon, { [s.noLabel]: !label }, classes.icon)}>
          {selectedIcon}
        </div>
      ) : null}
      {label ? <div className={cn(s.label, classes.label)}>{label}</div> : null}
    </Tag>
  );
};

ShadowButton.propTypes = {
  icon: element,
  label: string,
  onClick: func,
  disabled: bool,
  variant: oneOf(['outlined', 'filled', 'text']),
  color: oneOf(['green', 'blue', 'gray', 'red', 'white']),
  size: oneOf(['large', 'medium', 'small']),
  className: string,
  classes: shape({
    icon: string,
    label: string,
  }),
  hasIconLeft: bool,
  hasIconRight: bool,
  tagName: oneOfType([string, element]),
};

ShadowButton.defaultProps = {
  icon: null,
  label: '',
  color: 'blue',
  size: 'large',
  disabled: false,
  variant: 'outlined',
  onClick: noop,
  className: '',
  classes: {
    root: '',
    icon: '',
    label: '',
  },
  hasIconLeft: false,
  hasIconRight: false,
  tagName: 'button',
};

export default ShadowButton;
